import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useToast } from "@/providers/toast-provider";
import { checklistTemplateService } from "../services/checklistTemplate";
import type { CreateTemplatePayload, UpdateChecklistPayload } from "../types";
import { TemplateStatus } from "../types/template";
import { checklistTemplateQueryKeys } from "./useChecklistTemplateQueries";

type FleeterValidationError = {
	type: string;
	detail?: string;
	errors?: string[];
};

const getErrorData = (error: unknown) => {
	const axiosError = error as AxiosError<{ error: FleeterValidationError }>;
	return {
		axiosError,
		errorData: axiosError.response?.data?.error,
	};
};

const getSaveConflictToast = (apiMessage: string | undefined) => {
	const isLinkConflict = Boolean(apiMessage?.toLowerCase().includes("link"));

	return {
		title: isLinkConflict ? "Link já existe" : "Checklist já existe",
		description:
			apiMessage ||
			(isLinkConflict
				? "Já existe um checklist com este link."
				: "Já existe um checklist com este nome."),
	};
};

const showSaveErrorToast = (
	showToast: ReturnType<typeof useToast>["showToast"],
	error: unknown
) => {
	const { axiosError, errorData } = getErrorData(error);

	if (errorData) {
		const { type, detail, errors } = errorData;

		if (type === "FleeterValidationException" && errors?.length) {
			const uniqueErrors = Array.from(new Set(errors));

			showToast({
				variant: "error",
				title: "Erro de validação",
				description: uniqueErrors.join(" "),
			});
			return;
		}

		if (
			type === "ChecklistAlreadyExistsException" ||
			axiosError.response?.status === 409
		) {
			showToast({
				variant: "error",
				...getSaveConflictToast(detail || errors?.[0]),
			});
			return;
		}

		showToast({
			variant: "error",
			title: "Erro ao salvar formulário",
			description:
				detail || errors?.[0] || "Não foi possível salvar o formulário.",
		});
		return;
	}

	if (axiosError.response?.status === 409) {
		showToast({
			variant: "error",
			title: "Não foi possível salvar",
			description:
				"Este formulário não está mais disponível para edição (apenas rascunhos podem ser editados).",
		});
		return;
	}

	showToast({
		variant: "error",
		title: "Erro ao salvar formulário",
		description: "Não foi possível salvar o formulário. Tente novamente.",
	});
};

export const useCreateChecklistTemplate = () => {
	const queryClient = useQueryClient();
	const { showToast } = useToast();

	return useMutation({
		mutationFn: (payload: CreateTemplatePayload) =>
			checklistTemplateService.createTemplate(payload),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["checklistTemplates"] });
			const isDraft = variables.template.status === TemplateStatus.Draft;
			showToast({
				variant: "success",
				title: isDraft
					? "Rascunho salvo com sucesso!"
					: "Formulário publicado com sucesso!",
			});
		},
		onError: (error: unknown) => {
			showSaveErrorToast(showToast, error);
		},
	});
};

export const useUpdateChecklistTemplate = () => {
	const queryClient = useQueryClient();
	const { showToast } = useToast();

	return useMutation({
		mutationFn: ({
			templateId,
			payload,
		}: {
			templateId: number;
			payload: UpdateChecklistPayload;
		}) => checklistTemplateService.updateTemplate(templateId, payload),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["checklistTemplates"] });
			queryClient.removeQueries({
				queryKey: checklistTemplateQueryKeys.detail(variables.templateId),
			});
			const isDraft = variables.payload.status === TemplateStatus.Draft;
			showToast({
				variant: "success",
				title: isDraft
					? "Rascunho salvo com sucesso!"
					: "Formulário publicado com sucesso!",
			});
		},
		onError: (error: unknown) => {
			showSaveErrorToast(showToast, error);
		},
	});
};

export const useArchiveChecklistTemplate = () => {
	const queryClient = useQueryClient();
	const { showToast } = useToast();

	return useMutation({
		mutationFn: (templateId: number) =>
			checklistTemplateService.archiveTemplate(templateId),
		onSuccess: (_data, templateId) => {
			queryClient.invalidateQueries({ queryKey: ["checklistTemplates"] });
			queryClient.invalidateQueries({
				queryKey: ["checklistTemplate", templateId],
				refetchType: "none",
			});
			showToast({
				variant: "success",
				title: "Formulário arquivado com sucesso!",
			});
		},
		onError: (error: unknown) => {
			const { axiosError, errorData } = getErrorData(error);

			if (axiosError.response?.status === 409) {
				showToast({
					variant: "error",
					title: "Não foi possível arquivar",
					description:
						errorData?.detail ||
						errorData?.errors?.[0] ||
						"Apenas formulários ativos ou em rascunho podem ser arquivados.",
				});
				return;
			}

			showToast({
				variant: "error",
				title: "Erro ao arquivar formulário",
				description:
					errorData?.detail ||
					"Não foi possível arquivar o formulário. Tente novamente.",
			});
		},
	});
};
